import { useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';
import { useGetFirstAvailableSiteDomain } from '@atlassian/gic-anywhere';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import {
	ErrorStates,
	useBulkCreateContext,
	useSetError,
} from '../providers/BulkCreateContextProvider';
import { useAvailableSitesActions } from '../providers/BulkConfigureContextProvider';
import { getPersistedOverrideCloudId } from '../utils/localStorageHelpers';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents';

export const useGetAvailableSites = () => {
	const { setSelectedAvailableSite, setOptionsAvailableSites } = useAvailableSitesActions();
	const { cloudId: sessionCloudId } = useSessionData();

	const persistedOverrideCloudId = getPersistedOverrideCloudId();

	const overrideCloudIdForSitePicker = persistedOverrideCloudId || sessionCloudId;
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useBulkCreateContext();

	useGetFirstAvailableSiteDomain({
		preferredCloudId: overrideCloudIdForSitePicker,
		onComplete: useCallback(
			({ availableSites }: { availableSites: AvailableSite[] }) => {
				const firstSite = availableSites[0];
				const preferredSite = availableSites.find(
					(site: AvailableSite) => site.cloudId === overrideCloudIdForSitePicker,
				);
				void setSelectedAvailableSite(preferredSite || firstSite);
				void setOptionsAvailableSites(availableSites);
			},
			[setSelectedAvailableSite, setOptionsAvailableSites, overrideCloudIdForSitePicker],
		),
		onError: useCallback(
			(error: Error) => {
				logRequestFailed({ requestName: 'fetch-jira-site', errorMessage: error.message });
				void setError(ErrorStates.SITE_RETRIEVAL);
				void actions.setManualCreateState('error');
			},
			[logRequestFailed, setError, actions],
		),
	});
};
